import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/design/components/cr-data-view-table.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-dropdown-search-selection.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { takeUntil } from 'rxjs';
import { WorkTrackingAPI } from '../api/WorkTrackingAPI.js';
import { WorkTrackingAuslage, WorkTrackingAuslagenTypes } from '../entitites/WorkTrackingAuslage.js';
import { WorkTrackingTaetigkeitenState } from '../entitites/WorkTrackingTaetigkeitenState.js';

export type WorkTrackingAuslageChange = { auslage: WorkTrackingAuslage; note: string };

@customElement('chancen-dialog-edit-work-tracking-auslage')
export class DialogEditWorkTrackingAuslage extends ChromiumDialog<WorkTrackingAuslageChange> {
  static override get element_name() {
    return 'chancen-dialog-edit-work-tracking-auslage';
  }

  @property({ attribute: false }) public overrideTitle = new RXController(this, null as Maybe<string>);
  @property({ attribute: false }) public userID = null as Maybe<string>;
  @property({ attribute: false }) public fixedKPFs = null as Maybe<{ id: string; name: string }[]>;
  @property({ attribute: false }) public extended = new RXController(this, false);
  @property({ attribute: false }) public auslage = new RXController<WorkTrackingAuslage>(
    this,
    new WorkTrackingAuslage({
      stateContractor: WorkTrackingTaetigkeitenState.OPEN,
      stateResponsible: WorkTrackingTaetigkeitenState.OPEN,
    }),
  );

  @state() private _form = new FormController(this, this.auslage.observable, {
    onSuccess: doc => {
      this.close({ auslage: doc, note: this._note });
    },
  });

  @state() private _note = '';

  override heading = 'Taetigkeit bearbeiten';

  override connectedCallback(): void {
    super.connectedCallback();

    this.overrideTitle.observable.pipe(takeUntil(this.disconnected)).subscribe(t => {
      if (t) {
        this.heading = t;
      }
    });

    this.auslage.observable.pipe(takeUntil(this.disconnected)).subscribe(t => {
      if (this.overrideTitle.value) {
        return;
      }

      if (t._id) {
        this.heading = 'Taetigkeit bearbeiten';
      } else {
        this.heading = 'Neue Taetigkeit erstellen';
      }
    });
  }

  private _noteInput() {
    if (!this._form.document?._id) {
      return nothing;
    }

    return html`<cr-hr></cr-hr>
      <cr-input
        placeholder="Notiz"
        @value-picked=${(e: ValueEvent<string>) => (this._note = e.detail.value)}
      ></cr-input>`;
  }

  override content() {
    return html`<cr-flex space="sm">
      <cr-date-picker-dropdown
        .end-year=${DateTime.now().plus({ years: 5 }).year}
        placeholder="wann?"
        click-to-save
        flex
        clearable
        ${this._form.field('date')}
      ></cr-date-picker-dropdown>
      <cr-dropdown-selection
        ${this._form.field('type')}
        .selectables=${Object.values(WorkTrackingAuslagenTypes)}
        placeholder="Art"
        clearable
        @value-picked=${(e: ValueEvent<Maybe<string>>) => {
          if (!this._form.document) {
            return;
          }
          this._form.document.type = e.detail.value;
          this.requestUpdate();
        }}
      ></cr-dropdown-selection>
      <cr-input placeholder="Was" ${this._form.field('note')}></cr-input>
      ${!this.fixedKPFs
        ? html`<x-entity-picker
            flex
            placeholder="Standort"
            .options=${{
              class: KPF,
              operation: WorkTrackingAPI.searchKPF,
              selectionSet: {
                name: 1,
                kuerzel: 1,
                produkt: { _class: 1, kuerzel: 1 },
                school: { name: 1, kuerzel: 1 },
              },
              filter: new AdornisFilter({}),
            }}
            ${this._form.field('kpfID')}
          ></x-entity-picker>`
        : html`<cr-dropdown-selection
            flex
            placeholder="KPF"
            .selectables=${this.fixedKPFs.map(kpf => kpf.id)}
            .value=${this._form.document?.kpfID}
            .renderString=${(kpf: Maybe<string>) => this.fixedKPFs?.find(k => k.id === kpf)?.name ?? ''}
            ${this._form.field('kpfID')}
          ></cr-dropdown-selection>`}
      <cr-input type="number" placeholder="Kosten" ${this._form.field('money')}></cr-input>
      ${this._noteInput()}
      <cr-button
        @click=${() => {
          if (this.userID && this._form.document) {
            this._form.document.userID = this.userID;
          }
          this._form.validate();
        }}
        >Speichern</cr-button
      >
    </cr-flex>`;
  }
}
